import { useMemo, useRef } from "react";

import { PalButton, PalDialog, PalTooltip } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useTranslation } from "react-i18next";
import { MdInfo, MdLogout } from "react-icons/md";
import { useDispatch } from "react-redux";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DevicesIcon from "@mui/icons-material/Devices";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import PRTooltip from "~components/Generic/PRTooltip";
import { apiUrlDevice } from "~constants";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import { deleteDevice } from "~store/device/actions";

const allowedMetadataKeys = ["os", "browser", "language", "platform", "timezone", "type"];

function getStaticMapUrl(lat, lng) {
  const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";
  const params = {
    center: `${lat},${lng}`,
    zoom: 15,
    size: "550x200",
    markers: `color:red|${lat},${lng}`,
    key: window.pr_config?.google,
  };
  // Utils.qs fonksiyonu yoksa, params'i elle birleştir:
  const qs = Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");
  return `${baseUrl}?${qs}`;
}
function createGoogleMapsLink(latitude, longitude) {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

const MetadataDetailsModal = withCardon(
  function MetadataDetailsModalContent({ row, get }) {
    const { t } = useTranslation();
    const metadata = row.metadata;

    const keyValueDataList = useMemo(() => {
      const array = [];

      array.push({
        key: "first_login",
        label: t("dashboard.myDevices.firstLogin"),
        value: row.first_login ? DateHelper.getDateTimeLocal(row.first_login).format("LLT") : t("common.noData"),
      });
      array.push({
        key: "last_activity",
        label: t("dashboard.myDevices.lastActivity"),
        value: row.last_activity ? DateHelper.getDateTimeLocal(row.last_activity).format("LLT") : t("common.noData"),
      });
      array.push({
        key: "device_type",
        label: t("dashboard.myDevices.deviceType"),
        value: row.device_type,
      });
      array.push({
        key: "ip_address",
        label: t("dashboard.myDevices.ipAddress"),
        value: row.ip_address,
      });
      array.push({
        key: "registration_id",
        label: t("dashboard.myDevices.registrationId"),
        value: row.registration_id,
      });
      array.push({
        key: "timezone",
        label: t("dashboard.myDevices.timezone"),
        value: row.timezone,
      });
      if (metadata && typeof metadata === "object") {
        array.push(
          ...Object.entries(metadata)
            .filter(([key]) => allowedMetadataKeys.includes(key))
            .map(([key, value]) => ({
              key: `metadata.${key}`,
              label: key,
              value: typeof value === "object" ? JSON.stringify(value, null, 2) : String(value),
            }))
        );
      }
      return array;
    }, [row, t, metadata]);

    const hasLocation = row.latitude && row.longitude;

    return (
      <PalDialog fullWidth open maxWidth="sm">
        <PalDialog.Title>{t("common.metadataDetails")}</PalDialog.Title>
        <PalDialog.Content dividers>
          {hasLocation && (
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <a
                href={createGoogleMapsLink(row.latitude, row.longitude)}
                rel="noopener noreferrer"
                style={{ display: "inline-block" }}
                target="_blank"
              >
                <img
                  alt="Konum Haritası"
                  src={getStaticMapUrl(row.latitude, row.longitude)}
                  style={{ borderRadius: 8, maxWidth: "100%", marginBottom: 4 }}
                />
              </a>
              <div>
                <PRTooltip title={`Lat: ${row.latitude}, Lng: ${row.longitude}`}>
                  <span>
                    {t("dashboard.myDevices.latitude")}: {row.latitude} &nbsp;|&nbsp;{" "}
                    {t("dashboard.myDevices.longitude")}: {row.longitude}
                  </span>
                </PRTooltip>
              </div>
            </div>
          )}
          {metadata && typeof metadata === "object" ? (
            <Table size="small">
              <TableBody>
                {keyValueDataList.map(({ key, label, value }) => (
                  <TableRow key={key}>
                    <TableCell sx={{ fontWeight: "bold" }}>{label || key}</TableCell>
                    <TableCell>
                      {typeof value === "object" ? (
                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(value, null, 2)}</pre>
                      ) : (
                        String(value)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <span>{t("common.noData")}</span>
          )}
        </PalDialog.Content>
        <PalDialog.Actions>
          <PalButton onClick={get(false)}>{t("common.close")}</PalButton>
        </PalDialog.Actions>
      </PalDialog>
    );
  },
  { destroyOnHide: true }
);

const MyDevices = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tableRef = useRef();

  const columns = [
    {
      label: t("common.id"),
      key: "id",
    },
    {
      label: t("dashboard.myDevices.activeSession"),
      key: "is_active",
      render: (row) =>
        row.is_active ? <CheckCircleIcon color="success" titleAccess={t("dashboard.myDevices.active")} /> : null,
    },
    {
      label: t("dashboard.myDevices.deviceType"),
      key: "device_type",
    },
    {
      label: t("dashboard.myDevices.firstLogin"),
      key: "first_login",
      render: (item) =>
        item.first_login ? (
          <div>
            <PRTooltip title={DateHelper.getDateTimeLocal(item.first_login).format("LLT")}>
              <span>{DateHelper.getDateTimeLocal(item.first_login).fromNow()}</span>
            </PRTooltip>
          </div>
        ) : null,
    },
    {
      label: t("dashboard.myDevices.lastActivity"),
      key: "last_activity",
      render: (item) =>
        item.last_activity ? (
          <div>
            <PRTooltip title={DateHelper.getDateTimeLocal(item.last_activity).format("LLT")}>
              <span>{DateHelper.getDateTimeLocal(item.last_activity).fromNow()}</span>
            </PRTooltip>
          </div>
        ) : null,
    },
    {
      label: t("dashboard.myDevices.ipAddress"),
      key: "ip_address",
    },
    {
      label: t("dashboard.myDevices.country"),
      key: "country",
    },
    {
      label: t("dashboard.myDevices.city"),
      key: "city",
    },
    {
      label: t("dashboard.myDevices.region"),
      key: "region",
    },
    {
      label: t("dashboard.myDevices.currentDevice"),
      key: "current_device",
      render: (row) =>
        row.current_device ? (
          <PalTooltip title={t("dashboard.myDevices.thisDeviceDetail")}>
            <span>
              <DevicesIcon color="primary" />
            </span>
          </PalTooltip>
        ) : null,
    },
    {
      label: t("common.actions"),
      key: "actions",
      actions: true,
      fixed: "right",
      render: (row) => (
        <div className="d-flex justify-content-center gap-1">
          <PRButton
            outline
            color="secondary"
            icon={MdInfo}
            size="sm"
            tooltipText={t("common.details")}
            onClick={() => MetadataDetailsModal.show({ row: row })}
          />
          <PRButton
            outline
            color="danger"
            icon={MdLogout}
            size="sm"
            tooltipText={t("dashboard.myDevices.terminate")}
            onClick={async () => {
              if (
                await DialogHelper.showQuestionDelete(
                  t("dashboard.myDevices.terminateDeviceTitle"),
                  t("dashboard.myDevices.terminateDevice")
                )
              ) {
                await dispatch(deleteDevice(row.id));
                tableRef.current.refresh();
              }
            }}
          />
        </div>
      ),
    },
  ];
  const parentName = [
    {
      label: t("dashboard.myDevices"),
    },
  ];

  return (
    <PRContainer description name={t("common.settings")} parentName={parentName}>
      <PRTable ref={tableRef} columns={columns} url={apiUrlDevice.get} />
    </PRContainer>
  );
};

export default MyDevices;
